import classnames from 'classnames';
import { memo } from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  IconButton,
  CheckboxInput,
  DropdownInput,
  ArrowLeft as leftIcon,
  ArrowRight as rightIcon,
  IcNoDataEvent as noDataEventIcon,
  CalendarIcon as calendarIcon,
} from '@/shared/ui';
import useHrChartOptions from './useHrChartOptions';

type HrChartOptionsProps = {
  timezoneOffset: number;
  studyId: string;
  profileId: string;
  startReport: any;
  stopReport: any;
};

export type { HrChartOptionsProps };

/**
 * Navigation strip under the heart-rate chart for stepping through the study's recording days. Renders a skeleton while the chart or its options load, otherwise pairs previous and next arrows disabled at the ends of the daily count with a calendar dropdown of dates, and shows the AFib, artifact and no-data legend unless the day has no data.
 *
 * @param props - Options forwarded to useHrChartOptions for the chart's date navigation.
 * @returns The date navigator with its arrows, calendar dropdown and event legend.
 */
const HrChartOptions = ({
  timezoneOffset,
  studyId,
  profileId,
  startReport,
  stopReport,
}: HrChartOptionsProps) => {
  const {
    isLoadingHrChartOptions,
    isLoadingHrChart,
    dateIndex,
    dailyCount,
    dropdownOptions,
    formatSelectedDate,
    onChangeDate,
    handleClickArrow,
    isEmptyData,
    isAfibEvent,
  } = useHrChartOptions({ timezoneOffset, studyId, profileId, startReport, stopReport });

  if (isLoadingHrChartOptions || isLoadingHrChart) {
    return (
      <div className="navigator-skeleton-container">
        <div className="skeleton-container">
          <Skeleton height="100%" />
        </div>
      </div>
    );
  }

  return (
    <div id="hr-chart-options" className="ecg-options">
      <div className="w-33" />
      <div className="select-date w-33">
        <IconButton
          id="ecg-events-previous-date-btn"
          className="me-2"
          isFilled
          disabled={dateIndex === 0}
          iconComponent={<img src={leftIcon} alt="Left icon" />}
          onClick={() => {
            handleClickArrow(false);
          }}
        />
        <DropdownInput
          dropdownToggleId="ecg-events-date-dropdown-toggle"
          name="date"
          disabled={!dailyCount?.length}
          options={dropdownOptions}
          value={formatSelectedDate}
          onChange={onChangeDate}
          selectedItemIcon={<img src={calendarIcon} alt="Icon calendar" />}
          showCaret={false}
          direction="up"
          container="body"
        />
        <IconButton
          id="ecg-events-next-date-btn"
          className="ms-2"
          isFilled
          disabled={dateIndex === (dailyCount?.length ?? 0) - 1}
          iconComponent={<img src={rightIcon} alt="Right icon" />}
          onClick={() => {
            handleClickArrow(true);
          }}
        />
      </div>
      {isEmptyData ? (
        <div className="w-33" />
      ) : (
        <div className="event-info-container w-33">
          {isAfibEvent ? (
            <>
              <CheckboxInput
                className={classnames('event-info-checkbox', '--afib', 'always-visible')}
                id="afibEvent"
                name="afibEvent"
                data={{
                  label: 'AFib',
                  value: false as unknown as string, // pre-existing: CheckboxInput's data.value is string|number, this passes a boolean
                }}
                checked={false}
                onChange={() => {}}
              />
              {/* <CheckboxInput
                    className={classnames('event-info-checkbox', '--other', 'always-visible')}
                    id="otherEvent"
                    name="otherEvent"
                    data={{
                      label: 'Pause & VE events',
                      value: isShowOtherEvent,
                    }}
                    checked={isShowOtherEvent}
                    onChange={() => { toggleDisplayRhythmEvent(ENUM_EVENT_ACTION.OTHER_EVENT); }}
                  /> */}
            </>
          ) : (
            <>
              {/* <CheckboxInput
                    className={classnames('event-info-checkbox', '--other', 'always-visible')}
                    id="otherEvent"
                    name="otherEvent"
                    data={{
                      label: eventTypeConvert[activeButton],
                      value: false,
                    }}
                    checked={false}
                    onChange={() => { }}
                  /> */}
              <CheckboxInput
                className={classnames('event-info-checkbox', '--afib', 'always-visible')}
                id="afibEvent"
                name="afibEvent"
                data={{
                  label: 'AFib',
                  value: false as unknown as string, // pre-existing: CheckboxInput's data.value is string|number, this passes a boolean
                }}
                checked={false}
                onChange={() => {}}
              />
            </>
          )}
          <CheckboxInput
            className={classnames('event-info-checkbox', '--artifact', 'always-visible')}
            id="artifact"
            name="artifact"
            data={{
              label: 'Artifact',
              value: false as unknown as string, // pre-existing: CheckboxInput's data.value is string|number, this passes a boolean
            }}
            checked={false}
            onChange={() => {}}
          />
          <div className={classnames('event-info-checkbox', '--no-data')}>
            <img src={noDataEventIcon} alt="no-data-event" />
            <div>No data</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(HrChartOptions);
